import { useCallback, useEffect, useRef } from 'react';
import styles from './PrimaryMenu.module.scss';
import { useDevice } from '../../../../lib/hooks/device';
import WithDevice from '../../../common/WithDevice/WithDevice';
import { classes } from '../../../../lib/utils/cssUtils';
import ProfileMenu, { ProfileWelcomeMessage } from '../ProfileMenu/ProfileMenu';
import LogoutItem from '../../../common/MenuItem/LogoutItem';
import { useHeaderContext } from '../../../../lib/context/HeaderContext';
import MenuItem from '../../../common/MenuItem/MenuItem';
import { CY_PRIMARY_NAV } from '../../../../lib/constants/cypress';
import { Role, useSession } from '../../../../lib/context/SessionContext';
import DownloadsListCounter from '../../compound/Header/DownloadsListCounter';
import LocaleSwitcher from '../LocaleSwitcher/LocaleSwitcher';

export type PrimaryMenuProps = {
  id: string;
};

const PrimaryMenu = ({ id }: PrimaryMenuProps) => {
  const device = useDevice();
  const { payload } = useSession();
  const { role } = payload ?? {};
  const { setShowMobileMenu, showMobileMenu, primaryItems } = useHeaderContext();
  const navRef = useRef<HTMLDivElement>(null);

  const focusListener = useCallback((event: FocusEvent) => {
    event.stopPropagation();

    if (!navRef.current?.contains(event.relatedTarget as Node)) {
      setShowMobileMenu?.(false);
    }
  }, []);

  useEffect(() => {
    if (device !== 'desktop') {
      navRef.current?.addEventListener('focusout', focusListener);
    }

    return () => navRef.current?.removeEventListener('focusout', focusListener);
  }, [device]);

  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : '';
  }, [showMobileMenu]);

  if (!primaryItems.length) {
    return null;
  }

  return (
    <nav
      ref={navRef}
      className={classes(styles.primaryMenuContainer, {
        [styles.visibleMobile]: device !== 'desktop' && showMobileMenu,
      })}
      id={id}
      data-cy={CY_PRIMARY_NAV}
    >
      <ul className={styles.items}>
        <WithDevice mobile tablet>
          {role !== Role.GUEST && <ProfileWelcomeMessage className={styles.mobileWelcomeMessage} />}
          <ProfileMenu />
          <LocaleSwitcher />
          <DownloadsListCounter className={styles.mobileCounter} />
        </WithDevice>
        {primaryItems?.map(({ id: itemId, children, ...rest }, index) => (
          <MenuItem
            key={itemId}
            className={classes({
              [styles.mobileExtraPaddingTop]: !index,
              [styles.mobileExtraPaddingBottom]: index === primaryItems.length - 1,
            })}
            {...rest}
            subItems={children}
          />
        ))}
        {role !== Role.GUEST && (
          <WithDevice mobile tablet>
            <LogoutItem />
          </WithDevice>
        )}
      </ul>
    </nav>
  );
};

export default PrimaryMenu;
